import { Component, OnInit, OnDestroy, Inject, HostListener, ChangeDetectorRef } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { IonApp, IonRouterOutlet, Platform } from '@ionic/angular/standalone';
import { CommonModule } from '@angular/common';
import { HeaderComponent } from './components/header/header.component';
import { SideBarComponent } from './components/side-bar/side-bar.component';
import { TabBarComponent } from './components/tab-bar/tab-bar.component';
import { SplashScreenComponent } from './components/splash-screen/splash-screen.component';
import { AuthCheckService } from 'src/infrastructure/adapters/services/auth-check.service';
import { JwtService } from 'src/infrastructure/adapters/services/jwt.service';
import { DeviceSubscriptionService } from 'src/infrastructure/adapters/services/device-subscription.service';
import { CommuneVisualSettingsStateService } from 'src/infrastructure/adapters/services/commune-visual-settings-state.service';
import { AnalyticsService } from './services/analytics.service';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { App } from '@capacitor/app';
import { LocalNotifications } from '@capacitor/local-notifications';
import { SplashScreen } from '@capacitor/splash-screen';
import { Capacitor } from '@capacitor/core';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: true,
  imports: [IonApp, IonRouterOutlet, SideBarComponent, TabBarComponent, SplashScreenComponent, CommonModule],
})
export class AppComponent implements OnInit, OnDestroy {
  private routerSubscription?: Subscription;
  showSplash = true;

  get isLoggedIn(): boolean {
    return !this.jwtService.isTokenExpired();
  }

  constructor(
    @Inject(AuthCheckService) private authCheckService: AuthCheckService,
    private jwtService: JwtService,
    private deviceSubscription: DeviceSubscriptionService,
    private router: Router,
    private analytics: AnalyticsService,
    private platform: Platform,
    private cdr: ChangeDetectorRef,
    private visualSettingsState: CommuneVisualSettingsStateService
  ) {}

  async ngOnInit() {
    await this.platform.ready();
    this.visualSettingsState.start();

    // Mejorar detección de plataforma iOS y Android usando múltiples métodos
    const ionicIOSPlatform = this.platform.is('ios') || this.platform.is('ipad') || this.platform.is('iphone');
    const ionicAndroidPlatform = this.platform.is('android');
    const ionicNativePlatform = this.platform.is('hybrid') || this.platform.is('capacitor');
    
    // También usar Capacitor.getPlatform() como verificación adicional
    const capacitorPlatform = Capacitor.getPlatform();
    const isIOSPlatform = ionicIOSPlatform || capacitorPlatform === 'ios';
    const isAndroidPlatform = ionicAndroidPlatform || capacitorPlatform === 'android';
    const isNativePlatform = ionicNativePlatform || isIOSPlatform || isAndroidPlatform;
    
    console.log('Platform detection enhanced:', {
      ionicIOSPlatform,
      ionicAndroidPlatform,
      ionicNativePlatform,
      capacitorPlatform,
      isIOSPlatform,
      isAndroidPlatform,
      isNativePlatform,
      platformDetected: this.platform.platforms(),
      isCapacitorNative: Capacitor.isNativePlatform()
    });
    
    if (isNativePlatform) {
      // Splash nativo se auto-oculta a los 3s (configurado en capacitor.config.ts)
      // NO necesitamos SplashScreen.hide() manual
      
      // Mostrar splash personalizado INMEDIATAMENTE (estará tapado por el nativo hasta los 3s)
      this.showSplash = true;
      this.cdr.detectChanges();
      
      // Ocultar splash personalizado a los 4.5s (3s nativo + 1.5s personalizado)
      setTimeout(() => {
        this.showSplash = false;
        this.cdr.detectChanges();
      }, 4500);
      
    } else {
      // Web: Solo mostrar splash personalizado por 2 segundos
      this.showSplash = true;
      
      setTimeout(() => {
        this.showSplash = false;
      }, 2000);
    }

    // Check if user is already logged in and ensure device subscription
    if (!this.jwtService.isTokenExpired()) {
      console.log('[App] User already logged in, ensuring device subscription...');
      await this.deviceSubscription.ensureDeviceSubscription();
        // Inicializar listeners de notificaciones para campañas en Android/iOS
        this.deviceSubscription.setupNotificationListeners(
          async (notification) => {
            // Solo mostrar notificación local en Android y solo si hay título/mensaje
            if (Capacitor.getPlatform() === 'android' && notification?.title) {
              await LocalNotifications.requestPermissions();
              await LocalNotifications.schedule({
                notifications: [
                  {
                    title: notification.title,
                    body: notification.body || '',
                    id: Date.now(),
                    smallIcon: 'ic_launcher',
                    sound: undefined,
                    actionTypeId: '',
                    extra: notification.data || {}
                  }
                ]
              });
            }
            // Log para debug en cualquier plataforma
            console.log('[Notificación foreground]', notification);
          },
          (notificationTap) => {
            // Manejar tap en notificación
            console.log('[Notificación tap]', notificationTap);
          }
        );
    }

    // Start checking token expiration every 30 seconds
    this.authCheckService.startTokenExpirationCheck();

    // Automatic page view tracking
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const pagePath = event.urlAfterRedirects;
        const pageTitle = this.getPageTitle(pagePath);
        this.analytics.logPageView(pagePath, pageTitle);
      });

    // Handle app state changes on mobile (when app comes back from background)
    if (this.platform.is('hybrid') || this.platform.is('android') || this.platform.is('ios')) {
      App.addListener('appStateChange', async ({ isActive }) => {
        if (isActive && !this.jwtService.isTokenExpired()) {
          console.log('[App] App resumed, ensuring device subscription...');
          await this.deviceSubscription.ensureDeviceSubscription();
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
  }

  /**
   * Check when window gains focus (web) — throttled; no reintento agresivo de FCM.
   */
  @HostListener('window:focus')
  async onWindowFocus() {
    if (Capacitor.getPlatform() !== 'web') {
      return;
    }
    if (!this.jwtService.isTokenExpired()) {
      await this.deviceSubscription.ensureDeviceSubscription();
    }
  }

  /**
   * Close sidebar when clicking outside of it
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const sidebar = document.getElementById('sidebar');
    const target = event.target as HTMLElement;
    
    // Check if sidebar is open
    if (sidebar && sidebar.classList.contains('open')) {
      // Check if click is outside sidebar and not on menu button
      const clickedInsideSidebar = sidebar.contains(target);
      const clickedOnMenuButton = target.closest('.menu-button');
      
      if (!clickedInsideSidebar && !clickedOnMenuButton) {
        // Close the sidebar
        sidebar.classList.remove('open');
        setTimeout(() => {
          sidebar.style.display = 'none';
        }, 300);
      }
    }
  }

  /**
   * Get friendly page title from route path
   */
  private getPageTitle(pagePath: string): string {
    const titles: { [key: string]: string } = {
      '/': 'Inicio',
      '/route': 'Rutas',
      '/schedule': 'Horario',
      '/contact': 'Contacto',
      '/profile': 'Perfil',
      '/login': 'Iniciar Sesión',
      '/sign-up': 'Registro',
      '/recover-password': 'Recuperar Contraseña',
      '/new-password': 'Nueva Contraseña',
      '/change-password': 'Cambiar Contraseña'
    };

    // Check for exact match
    if (titles[pagePath]) {
      return titles[pagePath];
    }

    // Check for partial matches
    for (const [path, title] of Object.entries(titles)) {
      if (pagePath.startsWith(path) && path !== '/') {
        return title;
      }
    }

    return 'KDM';
  }
}